import React, { FC, Fragment, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { SelectOption } from '@/src/types/select-option';
import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/20/solid';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';
export interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  options: Array<SelectOption>;
  placeholder: string;
  label?: string;
  name: string;
  error?: string;
  success?: string;
  onSelectionChangeHook?: CallableFunction;
  labelIcon?: IconName;
  testId?: string;
  isMultiple?: boolean;
  alignOptionsTop?: boolean;
}
const Select: FC<SelectProps> = memo(function Select({
  options,
  name,
  id,
  required,
  placeholder,
  label,
  error,
  success,
  value,
  onSelectionChangeHook,
  labelIcon,
  testId,
  isMultiple,
  alignOptionsTop,
  ...props
}) {
  const evaluatedSelected = getOptionsFromValue(value, options);
  const [selectedOptions, setSelectedOptions] = useState<SelectProps['options']>(evaluatedSelected);
  const isMultiSelect = isMultiple && options.length > 1;
  let messageStyles = '';
  let inputStyles = '';
  if (error) {
    messageStyles += 'text-danger bg-danger-300';
    inputStyles += 'border-danger text-danger pr-12';
  }
  if (success) {
    messageStyles += 'text-success bg-success-300';
    inputStyles += 'border-success text-success pr-12';
  }
  useEffect(() => {
    const optionsFromValue = getOptionsFromValue(value, options);
    if (optionsFromValue !== selectedOptions) {
      setSelectedOptions(getOptionsFromValue(value, options));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);
  function getOptionsFromValue(value: string | number | ReadonlyArray<string> | null | undefined, options: Array<SelectOption>) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return options.filter(option => value.includes(option.value));
    }

    // single select
    return options.filter(option => value === option.value);
  }
  function isOptionSelected(option: SelectOption) {
    return selectedOptions.some(selectedOption => selectedOption === option);
  }
  const onSelectionChange = (options: Array<SelectOption>) => {
    options = Array.isArray(options) ? options : [options];
    setSelectedOptions(options);
    if (!onSelectionChangeHook) return;
    if (isMultiSelect) {
      onSelectionChangeHook(options);
      return;
    }
    if (options.length) onSelectionChangeHook(options[0]);
  };
  return <div className={props.className} data-testid={testId}>
      {/* @ts-ignore */}
      <Listbox value={selectedOptions} name={name} onChange={onSelectionChange} disabled={props.disabled || props.readOnly} multiple={isMultiSelect}>
        {({
        open
      }) => <div>
            {label ? <Label className="label mb-1" htmlFor={id || name}>
                {label}
                {required ? '*' : null}
              </Label> : null}

            <div className={classNames('relative bg-white', 'before:block before:w-[0.25rem] before:h-full before:absolute before:left-[1px] before:top-0 before:z-10', {
          'before:bg-black': selectedOptions.length || open
        })}>
              <ListboxButton className={classNames(inputStyles, 'input flex relative w-full cursor-default text-left pr-9 focus:border-black focus:outline-none focus:ring-1 focus:ring-black', {
            'bg-black border-black': open
          })}>
                {labelIcon ? <Icon name={labelIcon} className={classNames('mr-2', {
              'text-black': !selectedOptions.length && !open,
              'text-white': open
            })} /> : null}
                <span className={classNames('block truncate', {
              'text-gray-400': !selectedOptions.length && label && !open,
              'text-white': open
            })}>
                  {label && selectedOptions?.length ? selectedOptions?.map(option => `${option.label} `) : placeholder}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <Icon name={open ? IconName.EXPAND_LESS : IconName.EXPAND_MORE} className={classNames({
                'text-black': !open,
                'text-white': open
              })} aria-hidden="true" />
                </span>
              </ListboxButton>

              <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <ListboxOptions className={classNames('absolute z-20 max-h-60 w-full overflow-auto bg-white text-base border border-t-0 border-gray-300 focus:outline-none', {
              'bottom-full': alignOptionsTop
            })}>
                  {options.length ? options.map(option => <ListboxOption disabled={props.disabled || props.readOnly} key={option.value} className={({
                focus
              }) => classNames(focus ? 'border-l-black border-y-gray-300 bg-gray-100' : isOptionSelected(option) ? 'border-gray-100 border-l-primary bg-gray-100' : 'border-white', 'relative cursor-default border-l-4 border-y border-solid select-none py-2 pl-3 pr-9')} value={option}>
                          <span className={classNames(isOptionSelected(option) ? 'font-semibold' : 'font-normal', 'block truncate')}>
                            {option.label}
                          </span>

                          {isOptionSelected(option) ? <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span> : null}
                        </ListboxOption>) : null}
                </ListboxOptions>
              </Transition>
              {error || success ? <div className="mt-2">
                  <span className={classNames('text-xs', 'inline-block', 'py-1', 'px-2', messageStyles)}>
                    {error || success}
                  </span>
                </div> : null}
            </div>
          </div>}
      </Listbox>
    </div>;
});
export default Select;